import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Divider,
    Stack,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    useTheme,
    alpha
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Event as EventIcon,
    LocationOn as LocationIcon,
    CalendarToday as CalendarIcon,
    People as PeopleIcon,
    AttachMoney as MoneyIcon,
    CheckCircle,
    Cancel,
    Star,
    StarBorder,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    ConfirmationNumber as TicketIcon,
    PersonOutline as PersonIcon,
    BarChart as SalesIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ManageEventAdmin = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { user } = useAuth();

    const [activeTab, setActiveTab] = useState(0);
    const [event, setEvent] = useState(null);
    const [analytics, setAnalytics] = useState({});
    const [tickets, setTickets] = useState([]);
    const [attendees, setAttendees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        fetchEventData();
    }, [eventId]);

    const fetchEventData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch event details
            const eventResponse = await api.events.getEventById(eventId);
            if (eventResponse.success) {
                setEvent(eventResponse.data);
            }

            // Fetch analytics
            try {
                const analyticsResponse = await api.events.getEventAnalytics(eventId);
                if (analyticsResponse.success) {
                    setAnalytics(analyticsResponse.data);
                }
            } catch (err) {
                console.log('Analytics not available');
            }

            // Fetch tickets
            try {
                const ticketsResponse = await api.events.getEventTickets(eventId);
                if (ticketsResponse.success) {
                    setTickets(ticketsResponse.data);
                }
            } catch (err) {
                console.log('Tickets not available');
            }

            // Fetch attendees
            try {
                const attendeesResponse = await api.events.getEventAttendees(eventId);
                if (attendeesResponse.success) {
                    setAttendees(attendeesResponse.data);
                }
            } catch (err) {
                console.log('Attendees not available');
            }

        } catch (err) {
            console.error('Error loading event data:', err);
            setError('Failed to load event data');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            const response = await api.events.updateEventStatus(eventId, newStatus);
            if (response.success) {
                setEvent({ ...event, status: newStatus });
            }
        } catch (err) {
            console.error('Error updating status:', err);
        }
    };

    const handleToggleFeatured = async () => {
        try {
            const response = await api.events.toggleEventFeatured(eventId, !event.isFeatured);
            if (response.success) {
                setEvent({ ...event, isFeatured: !event.isFeatured });
            }
        } catch (err) {
            console.error('Error toggling featured:', err);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        try {
            return format(new Date(date), 'MMM dd, yyyy');
        } catch {
            return 'Invalid Date';
        }
    };

    const formatTime = (date) => {
        if (!date) return 'N/A';
        try {
            return format(new Date(date), 'HH:mm');
        } catch {
            return 'Invalid Time';
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'default',
            pending: 'warning',
            approved: 'success',
            rejected: 'error',
            active: 'info',
            completed: 'default',
            cancelled: 'error'
        };
        return colors[status] || 'default';
    };

    const filteredTickets = tickets.filter(ticket => {
        const buyerName = ticket.buyerId
            ? `${ticket.buyerId.firstName || ''} ${ticket.buyerId.lastName || ''}`.trim()
            : '';
        const matchesSearch = searchTerm === '' ||
            ticket.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            buyerName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === '' || ticket.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const filteredAttendees = attendees.filter(attendee => {
        return searchTerm === '' ||
            attendee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            attendee.email?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
            </Box>
        );
    }

    if (!event) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">Event not found</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/dashboard/event-management')}
                    sx={{ mb: 2 }}
                >
                    Back to Events
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" fontWeight="bold">
                        {event.title}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title={event.isFeatured ? "Remove from featured" : "Mark as featured"}>
                            <IconButton onClick={handleToggleFeatured} color={event.isFeatured ? "warning" : "default"}>
                                {event.isFeatured ? <Star /> : <StarBorder />}
                            </IconButton>
                        </Tooltip>
                        <Chip
                            label={event.status}
                            color={getStatusColor(event.status)}
                            sx={{ textTransform: 'capitalize' }}
                        />
                    </Stack>
                </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Overview" icon={<EventIcon />} iconPosition="start" />
                    <Tab label="Sales" icon={<SalesIcon />} iconPosition="start" />
                    <Tab label="Tickets" icon={<TicketIcon />} iconPosition="start" />
                    <Tab label="Attendees" icon={<PeopleIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {/* Overview Tab */}
            {activeTab === 0 && (
                <Grid container spacing={3}>
                    {/* Stats Cards */}
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 56, height: 56, mx: 'auto', mb: 2 }}>
                                    <TicketIcon sx={{ color: theme.palette.primary.main }} />
                                </Avatar>
                                <Typography variant="h4" fontWeight="bold">{event.soldTickets || 0}</Typography>
                                <Typography variant="body2" color="text.secondary">Tickets Sold</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), width: 56, height: 56, mx: 'auto', mb: 2 }}>
                                    <MoneyIcon sx={{ color: theme.palette.success.main }} />
                                </Avatar>
                                <Typography variant="h4" fontWeight="bold">₦{analytics.totalRevenue?.toLocaleString() || 0}</Typography>
                                <Typography variant="body2" color="text.secondary">Revenue</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), width: 56, height: 56, mx: 'auto', mb: 2 }}>
                                    <PeopleIcon sx={{ color: theme.palette.info.main }} />
                                </Avatar>
                                <Typography variant="h4" fontWeight="bold">{analytics.totalAttendees || 0}</Typography>
                                <Typography variant="body2" color="text.secondary">Attendees</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), width: 56, height: 56, mx: 'auto', mb: 2 }}>
                                    <CheckCircle sx={{ color: theme.palette.warning.main }} />
                                </Avatar>
                                <Typography variant="h4" fontWeight="bold">{analytics.checkedInCount || 0}</Typography>
                                <Typography variant="body2" color="text.secondary">Checked In</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Event Details */}
                    <Grid item xs={12} md={8}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">Event Details</Typography>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Description</Typography>
                                        <Typography variant="body1">{event.description}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Category</Typography>
                                        <Chip label={event.category} size="small" sx={{ mt: 0.5 }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Event Type</Typography>
                                        <Chip label={event.eventType} size="small" sx={{ mt: 0.5 }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Start Date</Typography>
                                        <Typography variant="body1">{formatDate(event.startDate)} at {formatTime(event.startDate)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">End Date</Typography>
                                        <Typography variant="body1">{formatDate(event.endDate)} at {formatTime(event.endDate)}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Venue</Typography>
                                        <Typography variant="body1">{event.venue?.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {typeof event.venue?.address === 'object'
                                                ? `${event.venue.address.street || ''}, ${event.venue.address.city || event.venue?.city || ''}`
                                                : `${event.venue?.address || ''}, ${event.venue?.city || ''}`
                                            }
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Vendor Info */}
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">Vendor Information</Typography>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body2" color="text.secondary">Business Name</Typography>
                                <Typography variant="body1" gutterBottom>{event.vendorId?.businessName || 'N/A'}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Contact Email</Typography>
                                <Typography variant="body1" gutterBottom>{event.vendorId?.contactEmail || 'N/A'}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Contact Phone</Typography>
                                <Typography variant="body1">{event.vendorId?.contactPhone || 'N/A'}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Sales Tab */}
            {activeTab === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>Sales Performance</Typography>
                    </Grid>

                    {[
                        { title: "Today's Sales", data: analytics.sales?.today, color: theme.palette.primary.main },
                        { title: "Yesterday's Sales", data: analytics.sales?.yesterday, color: theme.palette.info.main },
                        { title: "Last 7 Days", data: analytics.sales?.weekly, color: theme.palette.success.main },
                        { title: "Last 30 Days", data: analytics.sales?.monthly, color: theme.palette.warning.main },
                    ].map((item, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card sx={{ height: '100%', borderTop: `4px solid ${item.color}` }}>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        {item.title}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="h5" fontWeight="bold">
                                            ₦{item.data?.revenue?.toLocaleString() || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                            <TicketIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                            {item.data?.count || 0} tickets
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Sales Summary</Typography>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={4}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                                        <Typography variant="h4" fontWeight="bold" color="success.main">
                                            ₦{analytics.totalRevenue?.toLocaleString() || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">Total Tickets Sold</Typography>
                                        <Typography variant="h4" fontWeight="bold">
                                            {analytics.soldTickets || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">Total Attendees</Typography>
                                        <Typography variant="h4" fontWeight="bold" color="info.main">
                                            {analytics.attendeeCount || 0}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Tickets Tab */}
            {activeTab === 2 && (
                <Box>
                    <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                        <TextField
                            placeholder="Search tickets..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                            sx={{ flexGrow: 1 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="used">Used</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                        <IconButton onClick={fetchEventData}>
                            <RefreshIcon />
                        </IconButton>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ticket #</TableCell>
                                    <TableCell>Buyer</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Purchase Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTickets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No tickets found</TableCell>
                                    </TableRow>
                                ) : (
                                    filteredTickets.map((ticket) => (
                                        <TableRow key={ticket._id} hover>
                                            <TableCell>{ticket.ticketNumber}</TableCell>
                                            <TableCell>
                                                {ticket.buyerId
                                                    ? `${ticket.buyerId.firstName || ''} ${ticket.buyerId.lastName || ''}`.trim() || 'N/A'
                                                    : 'N/A'
                                                }
                                            </TableCell>
                                            <TableCell>{ticket.ticketTypeName}</TableCell>
                                            <TableCell>₦{ticket.totalAmount?.toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={ticket.status}
                                                    size="small"
                                                    color={ticket.status === 'active' ? 'success' : ticket.status === 'used' ? 'info' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell>{formatDate(ticket.purchaseDate)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Attendees Tab */}
            {activeTab === 3 && (
                <Box>
                    <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                        <TextField
                            placeholder="Search attendees..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                            sx={{ flexGrow: 1 }}
                        />
                        <IconButton onClick={fetchEventData}>
                            <RefreshIcon />
                        </IconButton>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Ticket Type</TableCell>
                                    <TableCell>Check-in Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAttendees.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">No attendees found</TableCell>
                                    </TableRow>
                                ) : (
                                    filteredAttendees.map((attendee, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{attendee.name || 'N/A'}</TableCell>
                                            <TableCell>{attendee.email || 'N/A'}</TableCell>
                                            <TableCell>{attendee.phone || 'N/A'}</TableCell>
                                            <TableCell>{attendee.ticketType || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={attendee.checkedIn ? 'Checked In' : 'Not Checked In'}
                                                    size="small"
                                                    color={attendee.checkedIn ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
};

export default ManageEventAdmin;
